/** Artifact contract for the ChatBox surface — an OPEN set: unknown `type`s route to a mandatory fallback renderer, never dropped. */

import type { ArtifactColumn } from "connected-components/dashboard/modules/ai-artifact/types"

/** The generic envelope every artifact satisfies; `type` is intentionally open (`string`). */
export interface ArtifactEnvelope {
  id: string
  type: string
  title?: string
  name?: string
  /** Gate for the working-files disclosure; no wire source emits it yet (see working-files.ts). */
  intermediate?: true
  [key: string]: unknown
}

export type Artifact = ArtifactEnvelope

export type { ArtifactColumn }

/** Deliberately looser than the dashboard's `PlotlyDataLayoutConfig` figure — narrowed further by APP-3359's renderer. */
export interface PlotFigure {
  data: unknown[]
  layout?: Record<string, unknown>
  [key: string]: unknown
}

export interface TableArtifact extends ArtifactEnvelope {
  type: "table"
  records: Record<string, unknown>[]
  columns: ArtifactColumn[]
  explainSteps?: string
}

export interface PlotArtifact extends ArtifactEnvelope {
  type: "plot"
  figure: PlotFigure
  tableArtifactId?: string
}

export interface TextArtifact extends ArtifactEnvelope {
  type: "text"
  text: string
}

/** Generic Claude.ai-style markdown artifact. No wire source today (closed upstream union — APP-3375); exercised via stories. */
export interface MarkdownArtifact extends ArtifactEnvelope {
  type: "markdown"
  markdown: string
}

/** Generic HTML artifact, rendered as inert literal text — markup is never executed. A producer wanting a live document should serve it through the panel route, as `file` does. No wire source today (APP-3375); exercised via stories. */
export interface HtmlArtifact extends ArtifactEnvelope {
  type: "html"
  html: string
}

export interface FileArtifact extends ArtifactEnvelope {
  type: "file"
  filename: string
  contentType?: string
  sizeBytes?: number
  /** Set only on an artifact opened from the Artifacts list: its share resource id (AI-734). */
  resourceId?: string
}

/**
 * The share resource an artifact is addressed by, or null for one that still belongs to a thread. The
 * two are served by different route families, so this is what picks between them.
 */
export function sharedArtifactResourceId(artifact: ArtifactEnvelope): string | null {
  return typeof artifact.resourceId === "string" && artifact.resourceId.length > 0 ? artifact.resourceId : null
}

/** The kinds with a dedicated panel renderer. NOT exhaustive — the set is open; unknown kinds route to the fallback. */
export type KnownArtifact = TableArtifact | PlotArtifact | TextArtifact | MarkdownArtifact | HtmlArtifact | FileArtifact

/** Narrow an `unknown` to an `ArtifactEnvelope`; deliberately does NOT restrict `type` to the known kinds. */
export function isArtifactEnvelope(value: unknown): value is ArtifactEnvelope {
  if (typeof value !== "object" || value === null) return false
  const candidate = value as { id?: unknown, type?: unknown, title?: unknown }
  if (typeof candidate.id !== "string" || typeof candidate.type !== "string") return false
  return candidate.title === undefined || typeof candidate.title === "string"
}

/** Payload guard for the `table` kind. */
export function isTableArtifact(artifact: ArtifactEnvelope): artifact is TableArtifact {
  return artifact.type === "table" && Array.isArray(artifact.records) && Array.isArray(artifact.columns)
}

/** Payload guard for the `plot` kind. */
export function isPlotArtifact(artifact: ArtifactEnvelope): artifact is PlotArtifact {
  if (artifact.type !== "plot") return false
  const { figure } = artifact
  return typeof figure === "object" && figure !== null && "data" in figure && Array.isArray(figure.data)
}

/** Payload guard for the `text` kind. */
export function isTextArtifact(artifact: ArtifactEnvelope): artifact is TextArtifact {
  return artifact.type === "text" && typeof artifact.text === "string"
}

/** Payload guard for the `markdown` kind. */
export function isMarkdownArtifact(artifact: ArtifactEnvelope): artifact is MarkdownArtifact {
  return artifact.type === "markdown" && typeof artifact.markdown === "string"
}

/** Payload guard for the `html` kind. */
export function isHtmlArtifact(artifact: ArtifactEnvelope): artifact is HtmlArtifact {
  return artifact.type === "html" && typeof artifact.html === "string"
}

export function isFileArtifact(artifact: ArtifactEnvelope): artifact is FileArtifact {
  if (artifact.type !== "file") return false
  if (typeof artifact.filename !== "string" || artifact.filename.length === 0) return false
  const contentType = artifact.contentType ?? artifact.content_type
  return typeof contentType === "string"
}

export function normalizeFileArtifact(
  artifact: FileArtifact,
): { filename: string, contentType: string, sizeBytes?: number } {
  const rawContentType = artifact.contentType ?? artifact.content_type
  const contentType = typeof rawContentType === "string" ? rawContentType : ""
  const rawSizeBytes = artifact.sizeBytes ?? artifact.size_bytes
  if (typeof rawSizeBytes === "number") {
    return { filename: artifact.filename, contentType, sizeBytes: rawSizeBytes }
  }
  return { filename: artifact.filename, contentType }
}

export function artifactMediaType(contentType: string): string {
  return contentType.split(";")[0].trim().toLowerCase()
}

const FILE_KIND_LABELS: Partial<Record<string, string>> = {
  "text/html": "HTML",
  "text/markdown": "Markdown",
  "text/csv": "CSV",
  "text/plain": "Text",
  "application/pdf": "PDF",
}

const ARTIFACT_KIND_LABELS: Partial<Record<string, string>> = {
  table: "Table",
  plot: "Chart",
  text: "Text",
  markdown: "Markdown",
  html: "Report",
}

export function artifactKindLabel(artifact: ArtifactEnvelope): string {
  if (isFileArtifact(artifact)) {
    const mediaType = artifactMediaType(normalizeFileArtifact(artifact).contentType)
    if (FILE_KIND_LABELS[mediaType] != null) return FILE_KIND_LABELS[mediaType]
    if (mediaType.startsWith("image/")) return "Image"
    return "File"
  }
  return ARTIFACT_KIND_LABELS[artifact.type] ?? "Artifact"
}
